import ModalComponent from 'components/Common/Modal';
import { useEffect, useRef, useState } from 'react';
import { BACKGROUND_MUSIC_TRACKS } from './ClipCreation/backgroundMusic';
import ClipPreviewPlayer from './ClipCreation/ClipPreviewPlayer';
import { generateClip, TARGET_VARIANT_HEIGHT } from './ClipCreation/generateClip';

const RESOLUTION_OPTIONS = [
  { value: 360, label: '360p' },
  { value: 480, label: '480p' },
  { value: 720, label: '720p' },
];

// $hover-blue in variables.scss
const BRAND_TEAL = '#0094cc';

const ClipProgressBar = ({ percent }) => (
  <div className='d-flex align-items-center' style={{ gap: 12 }}>
    <span className='paragraph-p4-bold text-white' style={{ minWidth: 40 }}>
      {percent}%
    </span>
    <div className='rounded-pill overflow-hidden flex-grow-1' style={{ height: 20, background: `${BRAND_TEAL}40` }}>
      <div
        className='h-100 rounded-pill'
        style={{ width: `${percent}%`, background: BRAND_TEAL, transition: 'width 0.3s ease' }}
      />
    </div>
  </div>
);

const CreateClipModal = (props) => {
  const { isOpen, setIsOpen, masterPlaylistUrl, logoUrl, item, itemVideoStart, itemVideoEnd, videoDuration } = props;

  const [musicOption, setMusicOption] = useState('none');
  const [resolutionOption, setResolutionOption] = useState(TARGET_VARIANT_HEIGHT);
  const [status, setStatus] = useState('idle'); // idle | loading | ready | error
  const [progress, setProgress] = useState({ message: '', percent: null, remainingSeconds: null });
  const [clipUrl, setClipUrl] = useState(null);
  const [videoReady, setVideoReady] = useState(false);
  const clipUrlRef = useRef(null);
  const downloadLinkRef = useRef(null);

  const clipStart = Math.max(0, itemVideoStart);
  const clipEnd = videoDuration ? Math.min(videoDuration, itemVideoEnd) : itemVideoEnd;
  const clipDuration = Math.max(0, clipEnd - clipStart);
  const musicTrack = BACKGROUND_MUSIC_TRACKS[musicOption] ?? null;

  const setClip = (blob) => {
    if (clipUrlRef.current) URL.revokeObjectURL(clipUrlRef.current);
    const url = URL.createObjectURL(blob);
    clipUrlRef.current = url;
    setVideoReady(false);
    setClipUrl(url);
  };

  const runGenerate = async () => {
    setStatus('loading');
    setProgress({ message: '', percent: null, remainingSeconds: null });
    try {
      const blob = await generateClip({
        masterPlaylistUrl,
        startSeconds: clipStart,
        endSeconds: clipEnd,
        logoUrl,
        musicTrack,
        targetHeight: resolutionOption,
        onProgress: setProgress,
      });
      setClip(blob);
      setStatus('ready');
    } catch (error) {
      setProgress({
        message: error?.message ?? 'Something went wrong generating the clip.',
        percent: null,
        remainingSeconds: null,
      });
      setStatus('error');
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setStatus('idle');
      setClipUrl(null);
    }
  }, [isOpen, item]);

  useEffect(() => {
    return () => {
      if (clipUrlRef.current) URL.revokeObjectURL(clipUrlRef.current);
    };
  }, []);

  const handleGenerateAndDownload = async () => {
    await runGenerate();
    setTimeout(() => downloadLinkRef.current?.click(), 0);
  };

  return (
    <ModalComponent isOpen={isOpen} setIsOpen={setIsOpen} maxWidth={900}>
      <h3 className='title-h4-bold mb-3 px-4 py-3 border-bottom'>Create a clip</h3>
      <div className='p-4'>
        <div className='d-lg-flex'>
          <div className='flex-grow-1 pr-lg-4 mb-3 mb-lg-0' style={{ minWidth: 0 }}>
            <div className='position-relative bg-main-blue rounded overflow-hidden' style={{ aspectRatio: '16/9' }}>
              {status === 'ready' && clipUrl ? (
                <>
                  <video
                    src={clipUrl}
                    controls
                    className='w-100 h-100'
                    style={{ objectFit: 'contain' }}
                    onLoadedData={() => setVideoReady(true)}
                  />
                  {!videoReady && (
                    <div
                      className='position-absolute w-100 h-100 flex-centered justify-content-center flex-column text-white text-center px-4'
                      style={{ top: 0, left: 0 }}
                    >
                      <div className='w-100' style={{ maxWidth: 320 }}>
                        <ClipProgressBar percent={100} />
                      </div>
                    </div>
                  )}
                </>
              ) : status === 'loading' ? (
                <div className='w-100 h-100 flex-centered justify-content-center flex-column text-white text-center px-4'>
                  <p className='mb-3'>{progress.message || 'Generating…'}</p>
                  <div className='w-100' style={{ maxWidth: 320 }}>
                    <ClipProgressBar percent={progress.percent ?? 0} />
                    {progress.remainingSeconds != null && (
                      <p className='paragraph-p5 mb-0 mt-2'>~{progress.remainingSeconds}s remaining</p>
                    )}
                  </div>
                </div>
              ) : status === 'error' ? (
                <div className='w-100 h-100 flex-centered justify-content-center text-white text-center px-3'>
                  {progress.message}
                </div>
              ) : isOpen ? (
                <ClipPreviewPlayer
                  key={`${item?.objectID}-${clipStart}-${clipEnd}`}
                  masterPlaylistUrl={masterPlaylistUrl}
                  clipStart={clipStart}
                  clipEnd={clipEnd}
                  logoUrl={logoUrl}
                  musicTrack={musicTrack}
                />
              ) : null}
            </div>
          </div>
          <div style={{ minWidth: '300px' }}>
            <div className='mb-4'>
              <label className='line-height-1-1 font-weight-bold paragraph-p1 d-block mb-2'>Background music</label>
              <select
                className='custom-select w-100'
                value={musicOption}
                onChange={(e) => setMusicOption(e.target.value)}
              >
                <option value='none'>None</option>
                {Object.keys(BACKGROUND_MUSIC_TRACKS).map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
            <div className='mb-3'>
              <label className='line-height-1-1 font-weight-bold paragraph-p1 d-block mb-2'>Resolution</label>
              <select
                className='custom-select w-100'
                value={resolutionOption}
                onChange={(e) => setResolutionOption(Number(e.target.value))}
              >
                {RESOLUTION_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
      <div className='border-top rounded-bottom px-4 py-3 d-flex justify-content-end bg-salmon'>
        <button
          className='general-button action-button rounded-pill px-4 specific-y-padding-5'
          disabled={status === 'loading'}
          onClick={handleGenerateAndDownload}
        >
          {status === 'loading' ? progress.message || 'Generating…' : 'Generate clip'}
        </button>
        {clipUrl && (
          <a
            ref={downloadLinkRef}
            href={clipUrl}
            download={`policymogul-clip-${item?.objectID ?? 'export'}.mp4`}
            className='d-none'
          >
            download
          </a>
        )}
      </div>
    </ModalComponent>
  );
};

export default CreateClipModal;
